import { useEffect, useState, useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSectionCompletion } from '../hooks/useSectionCompletion';
import { useOnboarding } from '../context/OnboardingContext';
import { UnsavedChangesContext } from '../hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { ONBOARDING_SECTIONS } from '../types/onboarding';

// Derive path→sectionId mapping from the single source of truth
const pathToSectionId: Record<string, string> = Object.fromEntries(
  ONBOARDING_SECTIONS.map((s) => [s.path, s.id])
);

export function SectionLayout() {
  const location = useLocation();
  const sectionId = pathToSectionId[location.pathname] || '';
  const { section, returnToDashboard, cameFromPortal } = useSectionCompletion(sectionId);
  const { markSectionViewed } = useOnboarding();

  // Unsaved changes guard state — set by child section pages via context
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  // Mark section as viewed (in-progress) when opened
  useEffect(() => {
    if (sectionId) {
      markSectionViewed(sectionId);
    }
  }, [sectionId, markSectionViewed]);

  // Guard the "Back to Dashboard" navigation
  const handleBackToDashboard = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowLeaveModal(true);
    } else {
      returnToDashboard();
    }
  }, [hasUnsavedChanges, returnToDashboard]);

  const handleStay = useCallback(() => {
    setShowLeaveModal(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setShowLeaveModal(false);
    returnToDashboard();
  }, [returnToDashboard]);

  // Memoize the context value so child effects don't re-fire on every parent render
  const unsavedChangesCtx = useMemo(
    () => ({ hasUnsavedChanges, setHasUnsavedChanges }),
    [hasUnsavedChanges]
  );

  return (
    <UnsavedChangesContext.Provider value={unsavedChangesCtx}>
      <div className="min-h-screen flex flex-col items-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-4xl">
          {/* Header */}
          <header className="mb-8">
            <button
              onClick={handleBackToDashboard}
              className="flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors mb-6"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {cameFromPortal ? 'Back to Portal' : 'Back to Dashboard'}
            </button>
            <h1 className="text-3xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-500">
              {section?.title || 'Section'}
            </h1>
            {section?.description && (
              <p className="text-gray-400 mt-2">{section.description}</p>
            )}
          </header>

          {/* Content */}
          <main className="animate-fade-in">
            <div className="card min-h-[400px] p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      <UnsavedChangesModal
        opened={showLeaveModal}
        onStay={handleStay}
        onDiscard={handleDiscard}
      />
    </UnsavedChangesContext.Provider>
  );
}
